import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { UploadSimple, Download, FileText, FileCsv } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function BulkImport() {
  var [importing, setImporting] = useState(false);
  var [result, setResult] = useState(null);
  var fileRef = useRef(null);

  var downloadTemplate = async function () {
    try {
      var response = await api.get('/offers/import-template', { responseType: 'blob' });
      var url = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'offer_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (err) { toast.error('Failed to download template'); }
  };

  var exportOffers = async function () {
    try {
      var response = await api.get('/offers/export', { responseType: 'blob' });
      var url = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'offers_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Offers exported');
    } catch (err) { toast.error('Failed to export offers'); }
  };

  var handleUpload = async function (e) {
    var file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      var formData = new FormData();
      formData.append('file', file);
      var response = await api.post('/offers/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(response.data);
      toast.success('Imported ' + response.data.imported + ' offers');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-import">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <UploadSimple size={32} className="text-blue-400" weight="duotone" />
          Bulk Offer Import / Export
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Import offers from CSV or export existing offers</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all">
          <CardContent className="p-5 text-center">
            <FileCsv size={32} className="text-amber-400 mx-auto mb-3" weight="duotone" />
            <h3 className="font-heading font-semibold text-white mb-1">Download Template</h3>
            <p className="text-xs text-zinc-400 mb-4">Get the CSV template with all fields</p>
            <Button data-testid="download-template" onClick={downloadTemplate} className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 gap-2" size="sm">
              <Download size={14} /> Template CSV
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all">
          <CardContent className="p-5 text-center">
            <UploadSimple size={32} className="text-blue-400 mx-auto mb-3" weight="duotone" />
            <h3 className="font-heading font-semibold text-white mb-1">Import Offers</h3>
            <p className="text-xs text-zinc-400 mb-4">Upload filled CSV to create offers</p>
            <input type="file" accept=".csv" ref={fileRef} onChange={handleUpload} className="hidden" data-testid="file-input" />
            <Button data-testid="upload-csv" onClick={function () { fileRef.current?.click(); }} disabled={importing} className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2" size="sm">
              {importing ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <UploadSimple size={14} />}
              {importing ? 'Importing...' : 'Upload CSV'}
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all">
          <CardContent className="p-5 text-center">
            <FileText size={32} className="text-emerald-400 mx-auto mb-3" weight="duotone" />
            <h3 className="font-heading font-semibold text-white mb-1">Export All Offers</h3>
            <p className="text-xs text-zinc-400 mb-4">Download all offers as CSV</p>
            <Button data-testid="export-offers" onClick={exportOffers} className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 gap-2" size="sm">
              <Download size={14} /> Export CSV
            </Button>
          </CardContent>
        </Card>
      </div>

      {result && (
        <Card className={'bg-zinc-900 border-zinc-800 border-l-4 ' + (result.errors > 0 ? 'border-l-amber-500' : 'border-l-emerald-500')}>
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base text-white">Import Results</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-zinc-950 rounded-lg p-3"><p className="text-[10px] text-zinc-500 uppercase">Imported</p><p className="text-lg font-bold text-emerald-400 font-heading">{result.imported}</p></div>
              <div className="bg-zinc-950 rounded-lg p-3"><p className="text-[10px] text-zinc-500 uppercase">Errors</p><p className="text-lg font-bold text-red-400 font-heading">{result.errors}</p></div>
              <div className="bg-zinc-950 rounded-lg p-3"><p className="text-[10px] text-zinc-500 uppercase">Total Rows</p><p className="text-lg font-bold text-blue-400 font-heading">{result.imported + result.errors}</p></div>
            </div>
            {result.error_details && result.error_details.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-zinc-400 font-medium mb-2">Error Details:</p>
                {result.error_details.map(function (err, i) {
                  return (
                    <div key={i} className="p-2 bg-red-500/5 border border-red-500/20 rounded text-xs text-red-400">
                      Row {err.row}: {err.error}
                    </div>
                  );
                })}
              </div>
            )}
            {result.offers && result.offers.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-zinc-400 font-medium mb-2">First imported offers:</p>
                <Table>
                  <TableHeader><TableRow className="border-zinc-800"><TableHead className="text-zinc-500 text-xs">Title</TableHead><TableHead className="text-zinc-500 text-xs">Type</TableHead><TableHead className="text-zinc-500 text-xs">Payout</TableHead><TableHead className="text-zinc-500 text-xs">ID</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {result.offers.map(function (o, i) {
                      return (
                        <TableRow key={i} className="border-zinc-800/50"><TableCell className="text-zinc-200 text-sm">{o.title}</TableCell><TableCell className="text-zinc-300 text-sm">{o.type}</TableCell><TableCell className="text-emerald-400 font-mono text-sm">${o.payout}</TableCell><TableCell className="text-zinc-500 font-mono text-xs">{o.offer_id}</TableCell></TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white">CSV Format Guide</CardTitle></CardHeader>
        <CardContent>
          <div className="bg-zinc-950 rounded-lg p-4 border border-zinc-800 font-mono text-xs text-blue-400 overflow-x-auto">
            <p className="text-zinc-500 mb-1"># Required columns: title, type, payout</p>
            <p className="text-zinc-500 mb-1"># Optional: description, category, geo_targets (semicolon-separated), url</p>
            <p className="mt-2">title,description,type,payout,category,geo_targets,url</p>
            <p>VPN Signup,High CPA VPN offer,CPA,45.00,SaaS,US;UK;CA,https://example.com</p>
            <p>Crypto Exchange,KYC completion lead,CPL,120.00,Finance,US;DE,https://crypto.example.com</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
